import { defineFakeRoute } from 'vite-plugin-fake-server/client';
import { accountRoutes } from './accountRoutes';
import { accountStore } from './store/account';
import { CaptchaError, createLoginCaptcha } from './store/loginCaptcha';
import { loginCaptchaConfig } from './store/loginCaptchaConfig';
import { type FakeRequest, waitTime } from './utils';

/**
 * 当前用户的权限，如果为空代表没登录
 * current user access， if is '', user need login
 * 如果是 pro 的预览，默认是有权限的
 */
const humanVerification = createLoginCaptcha(() => loginCaptchaConfig);

async function captchaResponse(work: () => unknown) {
  try {
    return { success: true, data: await work() };
  } catch (error) {
    return {
      success: false,
      errorCode: error instanceof CaptchaError ? error.code : 'CAPTCHA_UNAVAILABLE',
      showType: 0,
    };
  }
}

export default defineFakeRoute([
  ...accountRoutes,
  {
    url: '/login/challenge',
    method: 'post',
    response: ({ body }: FakeRequest) =>
      captchaResponse(() => humanVerification.issue(body)),
  },
  {
    url: '/login/challenge/verify',
    method: 'post',
    response: ({ body }: FakeRequest) =>
      captchaResponse(() => humanVerification.verify(body)),
  },
  {
    url: '/currentUser',
    method: 'get',
    response: () => {
      const user = accountStore.currentUser();
      if (!user.isLogin) {
        return {
          data: {
            isLogin: false,
            isSuperAdmin: false,
            permissions: [],
          },
          errorCode: '401',
          errorMessage: '请先登录！',
          success: true,
        };
      }
      return {
        success: true,
        data: {
          ...user,
        },
      };
    },
  },
  {
    url: '/users',
    method: 'get',
    response: () => [
      {
        key: '1',
        name: 'John Brown',
        age: 32,
        address: 'New York No. 1 Lake Park',
      },
      {
        key: '2',
        name: 'Jim Green',
        age: 42,
        address: 'London No. 1 Lake Park',
      },
      {
        key: '3',
        name: 'Joe Black',
        age: 32,
        address: 'Sidney No. 1 Lake Park',
      },
    ],
  },
  {
    url: '/login/account',
    method: 'post',
    response: async ({ body }: FakeRequest) => {
      const input: API.LoginParams & { humanVerificationToken?: string } = {
        ...body,
        type: body.type || 'account',
      };
      // Check after the simulated wait so concurrent requests see the latest counts.
      await waitTime(300);
      try {
        const rejected = humanVerification.beforeLogin(input);
        if (rejected)
          return { ...rejected, type: input.type, currentAuthority: 'guest' };
        const success =
          input.type === 'mobile'
            ? /^1\d{10}$/.test(input.mobile ?? '') && input.captcha === '123456'
            : accountStore.login(input.username ?? '', input.password ?? '');
        if (success && input.type === 'mobile') accountStore.loginMobile();
        return {
          ...humanVerification.afterLogin(input, success),
          type: input.type,
          currentAuthority: success
            ? input.type === 'mobile'
              ? 'admin'
              : input.username
            : 'guest',
        };
      } catch (error) {
        return {
          status: 'verification_required',
          humanVerificationRequired: true,
          verificationError:
            error instanceof CaptchaError ? error.code : 'CAPTCHA_UNAVAILABLE',
        };
      }
    },
  },
  {
    url: '/login/outLogin',
    method: 'post',
    response: () => {
      accountStore.logout();
      return { data: {}, success: true };
    },
  },
  {
    url: '/500',
    method: 'get',
    statusCode: 500,
    response: () => ({
      timestamp: 1513932555104,
      status: 500,
      error: 'error',
      message: 'error',
      path: '/base/category/list',
    }),
  },
  {
    url: '/404',
    method: 'get',
    statusCode: 404,
    response: () => ({
      timestamp: 1513932643431,
      status: 404,
      error: 'Not Found',
      message: 'No message available',
      path: '/base/category/list/2121212',
    }),
  },
  {
    url: '/403',
    method: 'get',
    statusCode: 403,
    response: () => ({
      timestamp: 1513932555104,
      status: 403,
      error: 'Forbidden',
      message: 'Forbidden',
      path: '/base/category/list',
    }),
  },
  {
    url: '/401',
    method: 'get',
    statusCode: 401,
    response: () => ({
      timestamp: 1513932555104,
      status: 401,
      error: 'Unauthorized',
      message: 'Unauthorized',
      path: '/base/category/list',
    }),
  },
  {
    url: '/login/captcha',
    method: 'get',
    response: async () => {
      await waitTime(2000);
      return 'captcha-xxx';
    },
  },
]);
